import type { EventStreamSubscription } from '@Application/Realtime/subscribeToEventStream';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import * as React from 'react';
import { type DefaultPayload, useDispatch } from '@verclam/synapse';

/**
 * Keeps an event stream open while `isReady` holds and `deps` are unchanged, and closes it on cleanup.
 * `subscribe` is read through a ref, so only `isReady` and `deps` restart the stream.
 */
const useEventSubscription = <Action extends DefaultPayload>(
	subscribe: () => EventStreamSubscription<Action>,
	isReady: boolean,
	deps: React.DependencyList,
): void => {
	const dispatch = useDispatch();
	const subscribeRef = React.useRef(subscribe);
	subscribeRef.current = subscribe;

	React.useEffect(() => {
		if (!isReady) {
			return undefined;
		}

		const subscription = subscribeRef.current();
		dispatch(subscription.start).catch(ignoreHandledError);

		return subscription.close;
	}, [dispatch, isReady, ...deps]);
};

export default useEventSubscription;
